import { useEffect } from 'react';

const SITE_NAME = 'Executive Storage Suites';

// Canonical URLs and og:url have to be absolute, and they have to agree with what the site is
// actually served from. public/index.html, robots.txt and sitemap.xml all use the same origin.
const SITE_ORIGIN = 'https://executivestoragesuites.com';

/**
 * Creates the tag if it is missing, otherwise updates the one already in the head. Tags written by
 * this hook are marked so a later page can tell them apart from the static defaults in index.html.
 */
function upsertMeta(attribute, name, content) {
    const selector = `meta[${attribute}="${name}"]`;
    let tag = document.head.querySelector(selector);

    if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attribute, name);
        document.head.appendChild(tag);
    }

    tag.setAttribute('content', content);
}

function upsertCanonical(href) {
    let link = document.head.querySelector('link[rel="canonical"]');

    if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
    }

    link.setAttribute('href', href);
}

/**
 * Sets the title, description, canonical URL and social preview tags for a page.
 *
 * Every static page used to set only document.title, which left all nine sharing the single
 * description and og:image declared in index.html. Crawlers and link unfurlers read whatever is in
 * the head at the time they look, so each page needs to write its own.
 * @param title {string} Page title, without the site name - that gets appended.
 * @param description {string} Meta and og:description. Aim for roughly 150-160 characters.
 * @param path {string} Route path for the canonical URL, e.g. '/features'. Defaults to the home page.
 */
export default function usePageMeta({ title, description, path = '/' }) {
    useEffect(() => {
        const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
        const canonical = `${SITE_ORIGIN}${path}`;

        document.title = fullTitle;

        upsertMeta('name', 'description', description);
        upsertCanonical(canonical);

        upsertMeta('property', 'og:title', fullTitle);
        upsertMeta('property', 'og:description', description);
        upsertMeta('property', 'og:url', canonical);

        upsertMeta('name', 'twitter:title', fullTitle);
        upsertMeta('name', 'twitter:description', description);
    }, [title, description, path]);
}
